import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { BehaviorSubject, interval, Subscription, timer } from 'rxjs';

import { ILoginData, ILoginParams, ILoginResponse } from './lib/models/login.model';
import { AppConfiguration } from './lib/services/app-config.service';
import { BaseHttpService } from './lib/services/base-http.service';
import { TAny } from './lib/models/common.type';
import { IResponse } from './lib/models/response.model';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Dialog } from '@angular/cdk/dialog';
import { DialogService } from './lib/services/dialog.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService extends BaseHttpService {

    private token: string | null = null;
    private refreshToken: string | null = null;
    private refreshSubscription!: Subscription;
    private _timerSubscriber!: Subscription;
    private _sessionLength = 30 * 60 * 1000;
    private baseUrl: string;


    public user: BehaviorSubject<ILoginData | null> = new BehaviorSubject<ILoginData | null>(null);

    constructor(
        http: HttpClient,
        appConfig: AppConfiguration,
        private router: Router,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
        private bottomSheet: MatBottomSheet,
        private cdkDialog: Dialog,
        private dialogService: DialogService
    ) {
        super(http, appConfig);
        this.baseUrl = this.appConfig.hostUrl;
        if (this.isAuthenticated()) {
            this.user.next(this.getData());
            this.updateAppConfig(this.getData())

        }
    }

    public isAuthenticated() {
        return this.getData() ? true : false;
    }

    public startTessionTimer() {
        if (this._timerSubscriber && !this._timerSubscriber.closed) {
            this._timerSubscriber.unsubscribe();
        }
        ////console.log('session started...!');
        this._timerSubscriber = timer(this._sessionLength)
            .subscribe(() => {
                ////console.log('user session timeout');
                this.logout();
                this.router.navigate(['/login']);
            });
    }

    validateUser(queryparams: ILoginParams) {
        return this.post<ILoginResponse | string, any>('auth/login-validation.htm', '', {
            params: { ...queryparams },
            withCredentials: true,
            headers: {
                'X-User-Id': queryparams.loginUsername,
                'X-Court-Id': `${4}`,
            },
            responseType: 'text' as 'json',
        });
    }


    setData(data: ILoginData) {
        localStorage.setItem('userData', JSON.stringify(data));
        this.updateAppConfig(this.getData());
    }

    getData(): ILoginData | null {
        try {
            const userData = localStorage.getItem('userData') as string;
            return userData ? JSON.parse(userData) : null;
        } catch (err) {
            return null
        }
    }

    storeToken(token: string, refreshToken: string): void {
        this.token = token;
        this.refreshToken = refreshToken;
        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', refreshToken);
    }

    getToken(): string | null {
        return this.token || localStorage.getItem('token');
    }

    scheduleTokenRefresh(token: string): void {
        const decodedToken = this.decodeToken(token);
        const expiryTime = (decodedToken.exp * 1000) - Date.now() - 60000; // Refresh 1 minute before expiry

        this.refreshSubscription = interval(expiryTime).subscribe(() => {
            this.refreshTokenRequest();
        });
    }

    decodeToken(token: string): any {
        const tokenPayload = token.split('.')[1];
        return JSON.parse(atob(tokenPayload));
    }

    refreshTokenRequest(): void {
        this.http.post('/api/refreshToken', { refreshToken: this.refreshToken })
            .subscribe({
                next: (res: any) => {
                    this.storeToken(res.token, res.refreshToken);
                    this.scheduleTokenRefresh(res.token);
                },
                error: (err) => {
                    console.error('Failed to refresh token', err);
                    this.handleSessionExpired();
                }
            });
    }

    logout(): void {
        this.post<any, any>('logoff-controller/logoff.htm', '', { responseType: 'text' })
            .subscribe({
                next: () => {
                    this.dialog.closeAll();
                    this.bottomSheet.dismiss();
                    this.snackBar.dismiss();
                    localStorage.removeItem('token');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('userData');
                    this.updateAppConfig(null);
                    this.router.navigate(['/login']);

                }
            });

    }

    isTokenExpired(token: string): boolean {
        const expirationDate = this.getTokenExpirationDate(token);
        if (!expirationDate) {
            return true; // Treat as expired if no expiration is found
        }
        return expirationDate.getTime() < Date.now();
    }

    private handleSessionExpired(): void {
        // Close all active overlays
        this.dialog.closeAll();
        this.bottomSheet.dismiss();
        this.snackBar.dismiss();

        // Navigate to login
        this.router.navigate(['/login']);

        // Show session-expired dialog
        this.dialogService.showDialog({
            title: 'Session Expired',
            message: 'Your session has expired. Please sign in again.',
            buttons: [{ key: 'ok', value: 'OK' }]
        });
    }

    public authCheck(token: string) {
        return this.post<ILoginResponse, any>('auth/token-validation.htm', '', {
            headers: {
                'X-User-Id': (this.appConfig.data.userName || this.appConfig.data.userId || ''),
                'X-Court-Id': this.appConfig.data.courtId,
                'Authorization': `Bearer ${token}`
            }
        });
    }

    private updateAppConfig(data: ILoginData | null) {
        if (data) {
            this.appConfig.updateProps({
                courtId: `${data.courtId}`,
                userId: `${data.activeUserId}`,
                userName: `${data.userName}`,
                token: `${data.token || null}`,
            });
        } else {
            this.appConfig.updateProps({
                courtId: '',
                userId: '',
                token: ''
            });
        }

    }

    private getTokenExpirationDate(token: string): Date | null {
        try {
            const decodedToken: JwtPayload = jwtDecode<JwtPayload>(token);
            if (!decodedToken.exp) {
                return null;
            }
            // Convert expiration time to milliseconds
            return new Date(decodedToken.exp * 1000);
        } catch (error) {
            console.error('Invalid token:', error);
            this.router.navigate(['/login']);
            return null;
        }
    }
    changePassword(oldPassword: string, newPassword: string, confirmedPassword: string) {

        const body = new HttpParams()
            .set('oldPassword', oldPassword)
            .set('newPassword', newPassword)
            .set('confirmedPassword', confirmedPassword);
        //         return this.post<any, any>('login/change-password.htm', body.toString());
        const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded', });
        const url = `${this.baseUrl}login/change-password.htm`;
        return this.http.post<TAny>(url, body, { headers });
    }
    forgotPassword(username: string, emailId: string) {

        const body = new HttpParams()
            .set('username', username)
            .set('emailId', emailId);
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded', 'X-User-Id': username || '',
            'X-Court-Id': this.appConfig.data.courtId,
        });
        const url = `${this.baseUrl}fpcontroller/forgot-password.htm`;
        return this.http.post<TAny>(url, body.toString(), { headers });
    }


}   
